import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ModuleLandingCompact, ContentWrapper, FavoritesAggregator } from '@textile-erp/ui';
import { Clock } from 'lucide-react';

export function RecentLanding() {
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);

  // Get aggregated recent items from all modules
  const categories = useMemo(() => {
    return FavoritesAggregator.getRecentItemsByModule();
  }, [refreshKey]);

  const handleNavigation = (path: string, label?: string, description?: string) => {
    // Track this navigation for recent items
    if (label) {
      FavoritesAggregator.trackRecentItem(path, label, description);
      // Refresh to update visit counts and timestamps
      setRefreshKey(prev => prev + 1);
    }
    navigate(path);
  };

  const handleFavoriteToggle = (path: string, isFavorite: boolean) => {
    // Find which module this path belongs to
    const moduleKey = inferModuleFromPath(path);
    if (!moduleKey) return;

    // Use the individual module's favorites hook to toggle
    const storageKey = `textile-erp-favorites-${moduleKey}`;
    try {
      const existing = localStorage.getItem(storageKey);
      const favorites: string[] = existing ? JSON.parse(existing) : [];
      
      if (isFavorite) {
        // Add to favorites
        if (!favorites.includes(path)) {
          favorites.push(path);
          localStorage.setItem(storageKey, JSON.stringify(favorites));
        }
      } else {
        // Remove from favorites
        const updatedFavorites = favorites.filter(fav => fav !== path);
        localStorage.setItem(storageKey, JSON.stringify(updatedFavorites));
      }
      
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };


  // Helper function to infer module from path
  const inferModuleFromPath = (path: string): string | null => {
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return null;
    
    // Convert first segment to module key format
    return segments[0].toLowerCase().replace(/[_\s]+/g, '-');
  };

  // Get current favorites for highlighting
  const allFavorites = useMemo(() => {
    const favorites = new Set<string>();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith('textile-erp-favorites-')) continue;
      
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const favPaths: string[] = JSON.parse(stored);
          favPaths.forEach(path => favorites.add(path));
        }
      } catch (error) {
        // Skip invalid entries
      }
    }
    return Array.from(favorites);
  }, [refreshKey]);

  const totalRecentItems = categories.reduce((total, cat) => total + cat.items.length, 0);

  return (
    <ContentWrapper
      breadcrumbs={[
        { label: 'Dashboard', onClick: () => navigate('/') },
        { label: 'Recent Items' }
      ]}
      noPadding={true}
    >
      <div className="h-full">
        {totalRecentItems > 0 ? (
          <ModuleLandingCompact
            title="Recent Items"
            description={`Your ${totalRecentItems} recently visited items organized by module`}
            categories={categories}
            onNavigate={handleNavigation}
            showSearch={true}
            showViewToggle={true}
            showExpandCollapseAll={true}
            showFavorites={true}
            initialFavorites={allFavorites}
            onFavoriteToggle={handleFavoriteToggle}
            defaultView="list" // List view is better for recent items to show timestamps and counts
            maxColumns={3}
            className="h-full"
          />
        ) : (
          <EmptyState onNavigate={navigate} />
        )}
      </div>
    </ContentWrapper>
  );
}

// Empty state when no recent items exist
function EmptyState({ onNavigate }: { onNavigate: (path: string) => void }) {
  const suggestedPages = [
    { label: 'User Management', path: '/users', description: 'Manage user accounts and permissions' },
    { label: 'Identity & Access', path: '/identity', description: 'Roles and permission management' },
    { label: 'UI Examples', path: '/ui-examples', description: 'Explore available UI components' }
  ];

  return (
    <div className="h-full flex items-center justify-center">
      <div className="max-w-md mx-auto text-center p-8">
        <div className="w-16 h-16 mx-auto mb-6 bg-primary-50 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
          <Clock className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        </div>
        
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          No Recent Activity
        </h2>
        
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Start exploring the application to see your recent activity here. 
          Items you visit will be automatically tracked and organized by module.
        </p>

        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Start exploring:
          </p>
          
          {suggestedPages.map((page, index) => (
            <button
              key={index}
              onClick={() => onNavigate(page.path)}
              className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-all group"
            >
              <div className="font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">
                {page.label}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {page.description}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
            How Recent Items Work
          </h3>
          <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 text-left">
            <li>• Automatically tracks pages you visit</li>
            <li>• Shows visit counts and time badges</li>
            <li>• Groups by module for better organization</li>
            <li>• Keeps items for up to 30 days</li>
          </ul>
        </div>
      </div>
    </div>
  );
}